import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { buildOrdinalMap, type OrdinalMap, type QuestionOrdinal } from "@/lib/questionOrdinals";

/**
 * Phase 2A closeout — reads the authoritative frozen question ordinals for an
 * assessment from `public.assessment_question_ordinals`.
 *
 * Ordinals are frozen at applicability resolution and are never recomputed
 * client-side. Rows without a frozen entry come back flagged as
 * `fallback_questionnaire_order`.
 */
export function useQuestionOrdinals(assessmentId?: string | null) {
  return useQuery({
    queryKey: ["question-ordinals", assessmentId],
    enabled: Boolean(assessmentId),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<OrdinalMap> => {
      const { data, error } = await (supabase.rpc as any)("assessment_question_ordinals", {
        p_assessment_id: assessmentId,
      });
      if (error) throw error;
      return buildOrdinalMap((data ?? []) as QuestionOrdinal[]);
    },
  });
}

/** Imperative variant for non-react-query call sites. */
export async function fetchQuestionOrdinals(assessmentId: string): Promise<OrdinalMap> {
  const { data, error } = await (supabase.rpc as any)("assessment_question_ordinals", {
    p_assessment_id: assessmentId,
  });
  if (error) throw error;
  return buildOrdinalMap((data ?? []) as QuestionOrdinal[]);
}
